import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, User, Star, CheckCircle, Clock, FileText, Video, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecruiterInterviewReview() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [jobTitle, setJobTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [expandedQ, setExpandedQ] = useState(null);
  const [playingUrl, setPlayingUrl] = useState('');
  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${API_URL}/api/recruiter/jobs/${jobId}/interviews`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const d = await r.json();
        setInterviews(d.interviews || []);
        setJobTitle(d.job_title || '');
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [jobId, token]);

  const getScoreColor = (score) => score >= 70 ? 'text-emerald-600 dark:text-emerald-400' : score >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400';
  const getScoreBg = (score) => score >= 70 ? 'bg-emerald-500/10 border-emerald-500/20' : score >= 50 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20';

  const playRecording = async (interviewId, questionIndex) => {
    try {
      const r = await fetch(`${API_URL}/api/recruiter/interviews/${interviewId}/recording-url?question_index=${questionIndex}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await r.json();
      if (d.url) {
        const fullUrl = d.url.startsWith('http') ? d.url : `${API_URL}${d.url}`;
        setPlayingUrl(fullUrl);
      }
    } catch (e) {
      alert('Failed to load recording');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Detail view for a selected interview
  if (selectedInterview) {
    const iv = selectedInterview;
    const ev = iv.evaluation;
    return (
      <div data-testid="interview-review-detail">
        <button onClick={() => { setSelectedInterview(null); setPlayingUrl(''); }} className="flex items-center gap-1 text-slate-500 dark:text-zinc-500 text-sm mb-4 hover:text-slate-600 dark:hover:text-zinc-300">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </button>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{iv.candidate_name}</h1>
            <p className="text-slate-500 dark:text-zinc-500 text-sm">{iv.candidate_email} &middot; Toughness {iv.toughness}/10 &middot; {iv.question_count} questions</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-1 rounded capitalize ${iv.status === 'evaluated' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : iv.status === 'completed' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>
              {iv.status}
            </span>
          </div>
        </div>

        {/* Score cards */}
        {ev && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              ['Overall', ev.overall_score],
              ['Functional', ev.functional_score],
              ['Technical', ev.technical_score],
              ['Behavioral', ev.behavioral_score],
            ].map(([label, score]) => (
              <div key={label} className={`border rounded-xl p-4 text-center ${getScoreBg(score)}`}>
                <p className="text-slate-500 dark:text-zinc-500 text-xs mb-1">{label}</p>
                <p className={`text-3xl font-bold ${getScoreColor(score)}`}>{score || '—'}</p>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {ev?.summary && (
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-4 mb-6">
            <h3 className="text-slate-900 dark:text-white text-sm font-medium mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" /> AI Summary</h3>
            <p className="text-slate-600 dark:text-zinc-300 text-sm leading-relaxed">{ev.summary}</p>
            {ev.recommendation && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-slate-500 dark:text-zinc-500 text-xs">Recommendation:</span>
                <span className={`text-xs px-2 py-0.5 rounded capitalize ${ev.recommendation === 'strong_hire' || ev.recommendation === 'hire' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : ev.recommendation === 'no_hire' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
                  {ev.recommendation.replace('_', ' ')}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Video player */}
        {playingUrl && (
          <div className="mb-6 bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl overflow-hidden">
            <video src={playingUrl} controls autoPlay className="w-full max-h-[400px]" data-testid="recording-player">
              Your browser does not support video playback.
            </video>
          </div>
        )}

        {/* Question-by-question review */}
        <h3 className="text-slate-900 dark:text-white text-sm font-medium mb-3">Answers ({iv.answers?.length || 0})</h3>
        <div className="space-y-3">
          {iv.answers?.map((ans, i) => {
            const ansEval = ev?.answer_evaluations?.[i];
            const isExpanded = expandedQ === i;
            return (
              <div key={i} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl overflow-hidden" data-testid={`answer-${i}`}>
                <button onClick={() => setExpandedQ(isExpanded ? null : i)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-100 dark:hover:bg-zinc-800/20 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 dark:text-zinc-600 text-xs font-mono">Q{i + 1}</span>
                    <span className="text-slate-600 dark:text-zinc-300 text-sm truncate max-w-[400px]">{ans.question_text}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {ans.recording_url && (
                      <button onClick={(e) => { e.stopPropagation(); playRecording(iv.id, ans.question_index); }}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded text-xs hover:bg-blue-500/20" data-testid={`play-recording-${i}`}>
                        <Play className="w-3 h-3" /> Watch
                      </button>
                    )}
                    {ansEval?.score && <span className={`text-sm font-medium ${getScoreColor(ansEval.score)}`}>{ansEval.score}</span>}
                    {ans.transcribed && <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded">STT</span>}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500 dark:text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-slate-500 dark:text-zinc-500" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-200 dark:border-zinc-800/40 pt-3 space-y-3">
                    <div>
                      <p className="text-slate-500 dark:text-zinc-500 text-xs mb-1">Candidate Answer {ans.transcribed ? '(Transcribed)' : ''}</p>
                      <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                        <p className="text-slate-600 dark:text-zinc-300 text-sm leading-relaxed">{ans.answer_text || <span className="text-slate-500 dark:text-zinc-600 italic">No text — watch the recording</span>}</p>
                      </div>
                    </div>
                    {ansEval && (
                      <div>
                        <p className="text-slate-500 dark:text-zinc-500 text-xs mb-1">AI Evaluation</p>
                        <div className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`text-lg font-bold ${getScoreColor(ansEval.score)}`}>{ansEval.score}/100</span>
                            {ansEval.category && <span className="text-xs px-1.5 py-0.5 bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400 rounded capitalize">{ansEval.category}</span>}
                          </div>
                          <p className="text-slate-500 dark:text-zinc-400 text-sm">{ansEval.feedback || ansEval.explanation || 'No feedback'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div data-testid="interview-review-list">
      <button onClick={() => navigate(`/recruiter/jobs/${jobId}`)} className="flex items-center gap-1 text-slate-500 dark:text-zinc-500 text-sm mb-4 hover:text-slate-600 dark:hover:text-zinc-300">
        <ArrowLeft className="w-4 h-4" /> Back to Job
      </button>

      <h1 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">Interview Recordings</h1>
      <p className="text-slate-500 dark:text-zinc-500 text-sm mb-6">{jobTitle} &middot; {interviews.length} interview{interviews.length !== 1 ? 's' : ''}</p>

      {interviews.length === 0 ? (
        <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-12 text-center">
          <Video className="w-10 h-10 text-slate-600 dark:text-zinc-700 mx-auto mb-3" />
          <p className="text-slate-500 dark:text-zinc-500">No interviews recorded yet</p>
          <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">Candidates will appear here after completing their interviews</p>
        </div>
      ) : (
        <div className="space-y-3">
          {interviews.map(iv => (
            <div key={iv.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-xl p-4 hover:border-slate-300 dark:hover:border-zinc-700/50 transition-colors cursor-pointer"
                 onClick={() => setSelectedInterview(iv)} data-testid={`interview-card-${iv.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-slate-900 dark:text-white font-medium text-sm">{iv.candidate_name}</p>
                    <p className="text-slate-500 dark:text-zinc-500 text-xs">{iv.candidate_email} &middot; {iv.question_count} answers &middot; Toughness {iv.toughness}/10</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {iv.jf_score > 0 && (
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${getScoreBg(iv.jf_score)}`}>
                      <Star className="w-3.5 h-3.5" />
                      <span className={`text-lg font-bold ${getScoreColor(iv.jf_score)}`}>{iv.jf_score}</span>
                    </div>
                  )}
                  <span className={`text-xs px-2 py-1 rounded capitalize ${iv.status === 'evaluated' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : iv.status === 'completed' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : iv.status === 'in_progress' ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-slate-100 dark:bg-zinc-800 text-slate-500 dark:text-zinc-400'}`}>
                    {iv.status === 'evaluated' ? <><CheckCircle className="w-3 h-3 inline mr-1" />Evaluated</> : iv.status === 'completed' ? <><Clock className="w-3 h-3 inline mr-1" />Evaluating...</> : iv.status}
                  </span>
                </div>
              </div>
              {iv.evaluation?.summary && (
                <p className="text-slate-500 dark:text-zinc-500 text-xs mt-3 line-clamp-2 ml-[52px]">{iv.evaluation.summary}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
